import { z } from 'zod';

/**
 * What a product *is* (vault Core Model, accepted change 2). Roles are structure, never
 * derived from a category name (lesson 5). A product can hold several (a loaf that is sold
 * and also used for croutons is `sellable` + `ingredient`).
 */
export const PRODUCT_ROLES = [
  'sellable',
  'ingredient',
  'supply',
  'packaging',
  'intermediate',
  'service',
] as const;
/** One of {@link PRODUCT_ROLES}. */
export type ProductRole = (typeof PRODUCT_ROLES)[number];

/** zod schema for a product role. */
export const productRoleSchema = z.enum(PRODUCT_ROLES);

/**
 * Roles that may be offered on a sales channel. `service` covers what a till sells that is
 * not stock (a delivery fee, a deposit, a custom-item placeholder).
 */
export const CHANNEL_ROLES: readonly ProductRole[] = ['sellable', 'service'];

/** True when `value` is a product role. */
export function isProductRole(value: string): value is ProductRole {
  return (PRODUCT_ROLES as readonly string[]).includes(value);
}

/** A problem with a product's role list. */
export type RoleIssue =
  | { code: 'no_roles' }
  | { code: 'unknown_role'; role: string }
  | { code: 'duplicate_role'; role: ProductRole };

/** Checks a role list: at least one role, each known, none repeated. */
export function validateRoles(roles: readonly string[]): RoleIssue[] {
  const issues: RoleIssue[] = [];
  if (roles.length === 0) issues.push({ code: 'no_roles' });
  const seen = new Set<string>();
  for (const role of roles) {
    if (!isProductRole(role)) issues.push({ code: 'unknown_role', role });
    else if (seen.has(role)) issues.push({ code: 'duplicate_role', role });
    seen.add(role);
  }
  return issues;
}

/** zod schema for a product's roles: non-empty, no repeats. */
export const productRolesSchema = z
  .array(productRoleSchema)
  .min(1, 'A product needs at least one role')
  .refine((roles) => new Set(roles).size === roles.length, 'Roles must not repeat');

/** Result of {@link channelVeto}. */
export type ChannelVeto =
  | { vetoed: false }
  | {
      vetoed: true;
      code: 'not_sellable';
      /** Why, in words a manager understands; stored with the mapping. */
      reason: string;
    };

const ROLE_LABEL: Record<ProductRole, string> = {
  sellable: 'sellable',
  ingredient: 'an ingredient',
  supply: 'a supply',
  packaging: 'packaging',
  intermediate: 'an intermediate (sub-recipe)',
  service: 'a service',
};

/**
 * Whether a product is barred from every sales channel. A product with no channel role
 * (`sellable` or `service`) is a **hard veto** on every channel, with a stored reason
 * (vault change 2). Being allowed is not the same as being listed: channel eligibility
 * still defaults off per channel.
 */
export function channelVeto(roles: readonly ProductRole[]): ChannelVeto {
  if (roles.some((r) => CHANNEL_ROLES.includes(r))) return { vetoed: false };
  const described =
    roles.length === 0 ? 'has no role' : `is ${roles.map((r) => ROLE_LABEL[r]).join(', ')}`;
  return {
    vetoed: true,
    code: 'not_sellable',
    reason: `Not for sale: this product ${described}, and only sellable or service products can be offered on a channel.`,
  };
}

/**
 * How a composite product consumes its components (vault change 6):
 * - `made_ahead`: produced as its own stock (a cake); components are consumed when the
 *   batch completes, and the product is deducted at sale. Also the mode of any product
 *   that is simply its own stock item (a bought-in drink, flour).
 * - `made_to_order`: made at the moment of sale (a coffee); components are deducted at sale.
 */
export const DEDUCTION_MODES = ['made_ahead', 'made_to_order'] as const;
/** One of {@link DEDUCTION_MODES}. */
export type DeductionMode = (typeof DEDUCTION_MODES)[number];
/** zod schema for a deduction mode. */
export const deductionModeSchema = z.enum(DEDUCTION_MODES);

/**
 * A product's place in its family (item, recipe twin, single, pack), so archiving one
 * member shows every other member first (vault change 1).
 */
export const FAMILY_ROLES = ['item', 'recipe_twin', 'single', 'pack'] as const;
/** One of {@link FAMILY_ROLES}. */
export type FamilyRole = (typeof FAMILY_ROLES)[number];
/** zod schema for a family role. */
export const familyRoleSchema = z.enum(FAMILY_ROLES);

/**
 * What a `service` product is (B-031; D-043): a till item that is sold but is never stock.
 * Only a `voucher` changes how VAT is worked out: a face-value voucher (gift card) carries
 * no VAT when sold, only when redeemed (UNVERIFIED(sars); D-043 still needs the
 * accountant). A fee, a tip, a deposit or anything else carries the product's own VAT rate
 * (a tip the till charges no VAT on is set `zero_rated`). `null` = not stated: the
 * product's VAT rate applies, so a fee is never mistaken for a voucher.
 */
export const SERVICE_KINDS = ['voucher', 'fee', 'tip', 'deposit', 'other'] as const;
/** One of {@link SERVICE_KINDS}. */
export type ServiceKind = (typeof SERVICE_KINDS)[number];
/** zod schema for a service kind. */
export const serviceKindSchema = z.enum(SERVICE_KINDS);

/**
 * A suggestion from a till item's name (an import plans it; a person can change it in the
 * Admin). `null` when the name doesn't say.
 */
export function guessServiceKind(name: string): ServiceKind | null {
  if (/\b(vouchers?|gift ?cards?|gift ?certificates?)\b/i.test(name)) return 'voucher';
  if (/\b(tips?|gratuity)\b/i.test(name)) return 'tip';
  if (/\bdeposits?\b/i.test(name)) return 'deposit';
  if (/\b(fees?|charges?|surcharges?)\b/i.test(name)) return 'fee';
  return null;
}

/**
 * Whether a sale of this product carries no VAT at the sale (a face-value voucher, D-043).
 * Only a `service` product stated to be a voucher does.
 */
export function vatNotChargedAtSale(p: {
  roles: readonly string[];
  serviceKind: ServiceKind | null;
}): boolean {
  return p.serviceKind === 'voucher' && p.roles.includes('service');
}
